'use client'
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { formatDateString, News } from "../utils/interfaces";
import { CircleAlert } from "lucide-react";

interface NewsListProps {
  path: string;
  infinite: boolean;
}

const NewsList: React.FC<NewsListProps> = ({path, infinite}) => {
  const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef<boolean>(false);

  const fetchArticles = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}${path}?page=${page}`);
      if (!response.ok) {
        throw new Error("Failed to fetch articles");
      }
      const data = await response.json();
      setNews((prevNews) => [...prevNews, ...data.results]);
      setHasMore(data.results.length > 0);
    } catch {
      setError("Мэдээ татахад алдаа гарлаа.");
    } finally {
      setLoading(false);
      isFetchingRef.current = false; // Reset fetch status
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [page]);

  useEffect(() => {
    if(!infinite) return;
    const handleScroll = () => {
      if (loading || !hasMore) return;
      const container = containerRef.current;
      if (container) {
        const bottom =
          container.scrollHeight === container.scrollTop + container.clientHeight;
        if (bottom) {
          setPage((prevPage) => prevPage + 1);
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loading, hasMore, infinite]);

  return (
    <>
      <div ref={containerRef} className={`overflow-auto h-[800px] [&::-webkit-scrollbar]:w-2
  [&::-webkit-scrollbar-track]:rounded-full
  [&::-webkit-scrollbar-track]:bg-gray-100
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:bg-gray-300
  dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500`}>
        {error && <p className="text-red-500">{error}</p>}
        <div className="grid grid-cols-1 gap-1 ">
          {news.length === 0 && !loading && <p>Мэдээ байхгүй байна.</p>}
          {news.map((article, index) => (
            <a
              key={index}
              className="overflow-hidden grid grid-cols-3 gap-3 p-2 rounded-sm me-1 bg-white hover:brightness-90 transition-all"
              href={`/news/${article.id}`}
            >
              <div className="relative col-span-1">
                <Image
                  src={article.thumbnail_image}
                  alt={article.title}
                  className="object-cover rounded-sm"
                  width={120}
                  height={90}
                />
              </div>
    
              <div className="col-span-2">
                <div className="text-xs text-gray-500">
                  {formatDateString(article.published_date)}
                </div>
                <h3 className="text-sm font-semibold text-black">
                  {article.is_live && <span className="text-red-500 uppercase font-bold me-1"><CircleAlert className="inline-block" size={18}/> Шууд</span>}
                  {article.title}</h3>
              </div>
            </a>
          ))}
        </div>
        {loading && <div className="flex justify-center items-center mt-4">
                        <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 border-secondary border-t-transparent rounded-full"></div>
                    </div>}
      </div>
    </>
  );
};

export default NewsList;
